import React from 'react';
import { Check, Video, Calendar as CalendarIcon } from 'lucide-react';
import { Button } from '../ui/button';
import { formatLongDate, formatTimeFromIso } from '../../lib/bookingTime';

function buildIcsHref({ start_iso, end_iso, title, description, meet_link }) {
  const stamp = (iso) => new Date(iso).toISOString().replace(/[-:]/g,'').replace(/\.\d+/,'');
  const lines = [
    'BEGIN:VCALENDAR', 'VERSION:2.0', 'PRODID:-//FGC//Booking//EN', 'BEGIN:VEVENT',
    `UID:${stamp(start_iso)}@foundergrowthcollective`,
    `DTSTAMP:${stamp(new Date().toISOString())}`,
    `DTSTART:${stamp(start_iso)}`,
    `DTEND:${stamp(end_iso)}`,
    `SUMMARY:${title}`,
    `DESCRIPTION:${(description || '').replace(/\n/g,'\\n')}${meet_link ? '\\nGoogle Meet: ' + meet_link : ''}`,
    meet_link ? `LOCATION:${meet_link}` : '',
    'END:VEVENT', 'END:VCALENDAR'
  ].filter(Boolean).join('\r\n');
  return 'data:text/calendar;charset=utf-8,' + encodeURIComponent(lines);
}

export default function Confirmation({ booking, date, slot, form }) {
  const ics = buildIcsHref({
    start_iso: booking.start_iso,
    end_iso: booking.end_iso || new Date(new Date(booking.start_iso).getTime()+30*60000).toISOString(),
    title: 'Discovery Call – Founder Growth Collective',
    description: `Discovery call with Hannah Hulley.`,
    meet_link: booking.meet_link,
  });

  return (
    <div className="max-w-2xl">
      <div className="w-14 h-14 rounded-full bg-[#B5876C]/15 border border-[#B5876C]/40 flex items-center justify-center mb-8">
        <Check size={22} className="text-[#B5876C]" strokeWidth={1.5} />
      </div>
      <h2 className="font-serif text-[44px] lg:text-[56px] leading-[1.02] tracking-[-0.015em] text-[#2E2C28] font-light">
        You’re <span className="italic text-[#B5876C]">booked.</span>
      </h2>
      <p className="mt-6 text-lg text-[#4a463f] font-light leading-relaxed max-w-md">
        A calendar invitation and Google Meet details have been sent to <span className="text-[#2E2C28]">{form.email}</span>.
      </p>

      <div className="mt-12 border border-[#d9d2ca] divide-y divide-[#d9d2ca]">
        <Row label="Session" value="Free Discovery Call · 30 minutes" />
        <Row label="Date" value={formatLongDate(date)} />
        <Row label="Time" value={`${formatTimeFromIso(slot.start_iso)} UK Time`} />
        <Row label="Location" value={booking.meet_link || 'Google Meet'} link={booking.meet_link} icon={<Video size={14} strokeWidth={1.5} />} />
      </div>

      <div className="mt-10 flex flex-wrap items-center gap-4">
        <Button asChild className="bg-[#2E2C28] hover:bg-[#B5876C] text-[#EBE4DE] rounded-none h-12 px-6 text-[12px] tracking-[0.2em] uppercase font-medium transition-colors">
          <a href={ics} download="discovery-call.ics" className="inline-flex items-center gap-2">
            <CalendarIcon size={14} strokeWidth={1.5} /> Add to Calendar
          </a>
        </Button>
        <a href="/" className="text-[12px] tracking-[0.2em] uppercase border-b border-[#3f3c37] pb-1 text-[#3f3c37] hover:text-[#B5876C] hover:border-[#B5876C] transition-colors">Back to home</a>
      </div>
    </div>
  );
}

function Row({ label, value, link, icon }) {
  return (
    <div className="flex items-center gap-6 px-5 py-4">
      <span className="text-[11px] tracking-[0.24em] uppercase text-[#7a7369] w-24 shrink-0">{label}</span>
      {link ? (
        <a href={link} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-sm text-[#2E2C28] hover:text-[#B5876C] transition-colors truncate">
          {icon}{value}
        </a>
      ) : (
        <span className="text-sm text-[#2E2C28] truncate inline-flex items-center gap-2">{icon}{value}</span>
      )}
    </div>
  );
}
